import { Input } from '../../ui/input.tsx';
import { Label } from '../../ui/label.tsx';
import { useBranding } from '../../../contexts/BrandingContext.tsx';
import { toast } from 'sonner';
import SaveBotton from './SaveBotton.tsx';
import LogoUpload from '@/components/branding/LogoUpload.tsx';
import { useState, useEffect } from 'react';

function Logo({ logoUrl, setLogoUrl, faviconUrl, setFaviconUrl, companyName, setCompanyName }: { logoUrl: string, setLogoUrl: (logoUrl: string) => void, faviconUrl: string, setFaviconUrl: (faviconUrl: string) => void, companyName: string, setCompanyName: (companyName: string) => void }) {
  const { branding, isLoading, isWhiteLabel, updateBranding } = useBranding();
  const [hasChanges, setHasChanges] = useState(false);

  useEffect(() => {
    setHasChanges(false); // Reset changes when component mounts
  }, []);

  const handleChange = (field: string, value: string) => {
    switch (field) {
      case 'companyName':
        setCompanyName(value);
        break;
      case 'logoUrl':
        setLogoUrl(value);
        break;
      case 'faviconUrl':
        setFaviconUrl(value);
        break;
    }
    setHasChanges(true); // Mark as changed
  };

  const handleSave = async () => {
    if (!isWhiteLabel) return toast.error('Only white-label partners can customize branding');
    const updatedBranding = {...branding, companyName, logoUrl, faviconUrl };
    await updateBranding(updatedBranding);
    setHasChanges(false); // Reset changes after successful save
  };

  return (
    <div>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Company Name */}
        <div className="lg:col-span-3">
          <Label>Company Name</Label>
          <Input
            value={companyName}
            onChange={(e) => handleChange('companyName', e.target.value)}
            placeholder="Your Company Name"
            className="max-w-md"
          />
        </div>

        {/* Logo Upload */} 
        <LogoUpload
          value={logoUrl}
          type="logo"
          title="Company Logo"
          description="Upload your company logo for branding. Recommended size: 200x50px."
          maxSize={5}
          acceptedFormats={['PNG', 'JPEG', 'JPG', 'SVG', 'WebP']}
          onChange={(value) => handleChange('logoUrl', value)}
        />

        {/* Favicon Upload */}
        <LogoUpload
          value={faviconUrl}
          type="favicon"
          title="Favicon"
          description="Upload your favicon. Recommended size: 32x32px."
          maxSize={1}
          acceptedFormats={['PNG', 'ICO', 'SVG']}
          onChange={(value) => handleChange('faviconUrl', value)}
        />

        {/* Brand Guidelines */}
        <div className="space-y-4">
          <div>
            <h4 className="font-medium mb-3">Brand Guidelines</h4>
            <div className="text-sm space-y-2 text-muted-foreground">
              <p>• Logo should be high resolution and readable at small sizes</p>
              <p>• Use transparent backgrounds for best results</p>
              <p>• Ensure sufficient contrast with your brand colors</p>
              <p>• Test on both light and dark backgrounds</p>
              <p>• SVG format is preferred for crisp scaling</p>
              <p>• Favicon should be simple and recognizable at 16x16px</p>
            </div>
          </div>

          {/* Legacy URL inputs for manual entry */}
          <div className="pt-4 border-t">
            <h5 className="font-medium mb-2 text-sm">Manual URL Entry</h5>
            <div className="space-y-3">
              <div>
                <Label className="text-xs">Logo URL (optional)</Label>
                <Input
                  value={logoUrl}
                  onChange={(e) => handleChange('logoUrl', e.target.value)}
                  placeholder="https://your-domain.com/logo.png"
                  className="text-xs"
                />
              </div>
              <div>
                <Label className="text-xs">Favicon URL (optional)</Label>
                <Input
                  value={faviconUrl}
                  onChange={(e) => handleChange('faviconUrl', e.target.value)}
                  placeholder="https://your-domain.com/favicon.ico"
                  className="text-xs"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
      <SaveBotton handleSave={handleSave} isSaving={isLoading} disabled={!hasChanges} />
    </div>
  )
}

export default Logo